import React, { FunctionComponent, useEffect, Fragment } from 'react';
import { ArrowDown, ArrowLongDown, Check, Stripe } from '../../../icons/components';
import classNames from '../../../utils/classnames';
import { useStepper } from '../../../utils/hooks';
import useMediaQuery from '../../../utils/hooks/use-media';
import { Button } from '../Button';

interface StepperStep {
    id: number;
}

export const StepperStep: FunctionComponent<StepperStep> = ({ children, id }) => {
    const { steps } = useStepper();
    const { isActive, isTouched, isValid } = steps[id];

    const classes = classNames('stepper__step', {
        'stepper__step--is-active': isActive,
        'stepper__step--is-valid': isValid,
        'stepper__step--is-touched': isTouched,
    });

    return <div className={classes}>{children}</div>;
};

export const StepperContent: FunctionComponent = ({ children }) => (
    <div className="stepper__step-content">{children}</div>
);

export const StepperHeader: FunctionComponent<{
    id: number;
    title: string;
    completedText?: string;
}> = ({ id, title, completedText }) => {
    const { steps } = useStepper();
    const { isActive, isTouched, isValid } = steps[id];
    const { setStepActive } = useStepper();

    return (
        <div className="stepper__step-header  u-flex u-flex-middle u-flex-between">
            <div className="u-flex u-flex-middle">
                <span className="stepper__step-checkmark">
                    <Check height={12} width={16} />
                </span>
                <span className="stepper__step-title">{title}</span>
            </div>

            <div className="u-flex u-flex-middle">
                {completedText && (
                    <span className="stepper__step-completed-title">{completedText}</span>
                )}

                {(isActive || isTouched || isValid) && (
                    <button
                        className={`stepper__step-button ${
                            !isActive ? 'stepper__step-button--open' : ''
                        }`}
                        title={!isActive ? 'Openen' : 'Sluiten'}
                        onClick={() => {
                            setStepActive(id, !isActive);
                        }}>
                        {!isActive && <ArrowDown width={13} height={8} />}
                        {isActive && <Stripe width={12} height={3} />}
                    </button>
                )}
            </div>
        </div>
    );
};

export const StepperSubmit: FunctionComponent<{
    isValidated?: boolean;
    id: number;
    variant?: 'primary';
    isLastStep?: boolean;
    isLoading?: boolean;
    label?: string;
    onStepChange?: () => void;
}> = ({ isValidated, id, variant, isLastStep, isLoading, onStepChange, label }) => {
    const { setCurrentStep, setStepActive, setStepValid } = useStepper();

    function setNextStep() {
        setStepValid(id, true);
        if (!isLastStep) setCurrentStep(id + 1);
        setStepActive(id, false);
        if (onStepChange) onStepChange();
    }
    return (
        <Fragment>
            {variant === 'primary' && (
                <div className="stepper__step-button-holder">
                    <Button
                        label={label || ''}
                        disabled={!isValidated}
                        onClick={setNextStep}
                        isLoading={isLoading}
                    />
                </div>
            )}
            {!variant && (
                <div className="stepper__step-button-holder">
                    <button
                        type="submit"
                        className="stepper__step-next-icon"
                        disabled={!isValidated}
                        onClick={setNextStep}>
                        <ArrowLongDown width={10} height={12} />
                    </button>
                </div>
            )}
        </Fragment>
    );
};

export const Stepper: FunctionComponent<{
    defaultUrl: string;
    previousButtonTexts: string[];
    defaultPreviousText: string;
    activeStep?: number;
}> = ({ children, defaultUrl, defaultPreviousText, previousButtonTexts, activeStep }) => {
    const { setSteps, steps, currentStep, setCurrentStep, setStepActive } = useStepper();
    const isScreenTabletAndLower = useMediaQuery('(max-width: 63.96875em)');

    const hasCurrentStep = currentStep && currentStep !== 0;

    function handleBackClick() {
        if (currentStep && currentStep !== 0) {
            const id = currentStep - 1;
            setStepActive(currentStep, false);
            setCurrentStep(id);
        } else {
            window.location.href = defaultUrl;
        }
    }

    useEffect(() => {
        const stepperSteps = React.Children.toArray(children).map((step: any) => {
            return {
                ...step.props,
                isValid: false,
                isActive: activeStep ? activeStep === step.props.id : currentStep === step.props.id,
                isTouched: activeStep
                    ? activeStep === step.props.id
                    : currentStep === step.props.id,
            };
        });
        setSteps(stepperSteps);
    }, []); // eslint-disable-line

    return (
        <div className="c-stepper">
            {steps.length > 1 && (
                <div className="stepper__indicator">
                    {steps.map((step, key) => (
                        <span
                            key={key}
                            className={`stepper__indicator-item ${
                                step.isTouched ? 'stepper__indicator-item--is-valid' : ''
                            }`}></span>
                    ))}
                </div>
            )}
            <div className="stepper__wrapper">
                {steps.length > 1 && (
                    <button className="stepper__back-button" onClick={handleBackClick}>
                        <span className="stepper__back-button-icon">
                            <ArrowLongDown width={12} />
                        </span>
                        {isScreenTabletAndLower && hasCurrentStep && currentStep
                            ? previousButtonTexts[currentStep - 1]
                            : defaultPreviousText}
                    </button>
                )}
                {steps.length > 0 && children}
            </div>
        </div>
    );
};
